import { useTranslations } from 'next-intl';
import Breadcrumb from '@/components/ui/Breadcrumb';
import { formatRelativeDate } from '@/lib/utils/dateHelpers';
import type { BreadcrumbItem } from '@/lib/data/types';
import type { PlaylistContentData } from '@/lib/data/loaders/mongodb/playlists';

interface PlaylistHeroProps {
  playlistContent: PlaylistContentData;
  locale?: string;
  breadcrumbItems?: BreadcrumbItem[];
}

const PlaylistHero = ({ playlistContent, locale = 'en', breadcrumbItems }: PlaylistHeroProps) => {
  const t = useTranslations('common');

  // Use provided breadcrumb items or build default ones
  const finalBreadcrumbItems: BreadcrumbItem[] = breadcrumbItems || [
    { title: t('global.commonTerms.playlists'), href: '/playlists' },
    { title: playlistContent.name, href: `/playlists/${playlistContent.slug}` },
  ];

  return (
    <section className="text-center position-relative py-5" data-testid="playlist-hero">
      <div className="container position-relative zindex-2 pt-5 pb-md-2 pb-lg-4 pb-xl-5 border-bottom mb-5">
        {/* Breadcrumbs */}
        <div data-testid="playlist-breadcrumb">
          <Breadcrumb items={finalBreadcrumbItems} />
        </div>
        
        <div className="row pt-3 pb-2 py-md-4">
          {/* Left Side (Text) */}
          <div className="col-xl-5 col-md-6 text-center text-md-start order-md-1" data-testid="hero-text-section">
            <p className="fs-sm" data-testid="hero-subtitle">
              {t('global.commonTerms.theBest')} Spotify {t('global.commonTerms.playlists')}
            </p>
            
            <h1
              className="mb-0"
              dangerouslySetInnerHTML={{ __html: playlistContent.hero.headline }}
              data-testid="hero-headline"
            />
            
            <div className="d-flex align-items-center my-4 py-2" data-testid="hero-actions">
              <a
                href={playlistContent.spotifyPublicUrl || `https://open.spotify.com/playlist/${playlistContent.spotifyPlaylistId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-gradient d-inline-flex align-items-center rounded-5"
                data-testid="add-to-spotify-button"
              >
                <i className="bx bxl-spotify fs-xl me-2" style={{ fontSize: '24px' }}></i>
                {t('global.actions.addToSpotify')}
              </a>
              
              {playlistContent.updated_at && (
                <span className="d-flex align-items-center ms-3" data-testid="last-updated">
                  <i className="bx bx-time-five me-2"></i>
                  {t('global.commonTerms.lastUpdated')} {formatRelativeDate(playlistContent.updated_at)}
                </span>
              )}
            </div>
            
            <div
              className="mb-0 fs-sm"
              dangerouslySetInnerHTML={{ __html: playlistContent.hero.subheadline }}
              data-testid="hero-subheadline"
            />
          </div>
          
          {/* Right Side (Spotify Iframe) */}
          <div className="col-xl-7 col-md-6 pe-md-5 mt-4 mt-md-0" data-testid="hero-spotify-section">
            {playlistContent.spotifyPlaylistId && (
              <iframe
                style={{ borderRadius: '12px' }}
                src={`https://open.spotify.com/embed/playlist/${playlistContent.spotifyPlaylistId}?utm_source=generator`}
                width="100%"
                height="370"
                frameBorder="0"
                allowFullScreen
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
                title={t('playlists.pages.detail.components.hero.spotifyPlaylist', { name: playlistContent.name })}
                data-testid="hero-spotify-embed"
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PlaylistHero; 